import {
  ACTION,
  Box,
  EMPTY_ARRAY,
  Flex,
  FormControlSizes,
  HelpIcon,
  HStack,
  IconButton,
  IconName,
  OrderPresetIcon,
  Stack,
  SubAccountIcon,
  UnifiedLiquidityEnum,
  useAggregationsContext,
  useMarketAccountsContext,
  useMarketsContext,
  useSecuritiesContext,
  useUnifiedLiquidity,
  useUnifiedLiquidityAvailableSymbols,
} from '@talos/kyoko';
import { EllipsisBoxWithTooltip } from '@talos/kyoko/src/components/Form/BaseSelect/EllipsisBoxWithTooltip';
import type { Security } from '@talos/kyoko/src/types';
import { useMemo, type Dispatch, type SetStateAction } from 'react';
import { useRoleAuth } from '../../../../../hooks/useRoleAuth';
import { useOrderPresets } from '../../../../../providers/OrderPresetsContext';
import { useTradingSettings } from '../../../../../providers/TradingSettingsContext';
import { MarketCardDisplayLabels } from '../../../../MarketDataCard/MarketCardDisplayLabels';
import { Title } from '../../../../SymbolDisplay/styles';
import { SymbolHeader } from '../../../../SymbolDisplay/SymbolHeader';
import { SymbolSubhead } from '../../../../SymbolDisplay/SymbolSubhead';
import type { SymbolDisplayProps } from '../../../../SymbolDisplay/types';
import { PanelHeader } from './styles';

interface SingleSymbolPanelHeaderProps {
  security: Security | undefined;
  aggregation: string | undefined;
  markets: string[] | undefined;
  marketAccounts: string[] | undefined;
  showSettings: boolean;
  setShowSettings: Dispatch<SetStateAction<boolean>>;
  marketsTooltip?: React.ReactNode;
  enableUnifiedLiquidity: boolean | undefined;
  orderPresetID: string | undefined;
  subAccount: string | undefined;
  panelTypeLabel: string;
}

const FONT_STYLES = {
  fontSize: 'fontSizeSmall',
  color: 'colorTextSubtle',
  lineHeight: 1,
};

export function SingleSymbolPanelHeader({
  security,
  aggregation,
  markets = [],
  marketAccounts = [],
  showSettings,
  setShowSettings,
  enableUnifiedLiquidity,
  marketsTooltip = 'With no selected exchanges, market data for all exchanges will be used',
  orderPresetID,
  panelTypeLabel,
  subAccount,
}: SingleSymbolPanelHeaderProps) {
  const { securitiesBySymbol } = useSecuritiesContext();
  const { orderPresetsByID } = useOrderPresets();
  const { marketDisplayNameByName } = useMarketsContext();
  const { marketAccountDisplayNameByName } = useMarketAccountsContext();
  const { aggregationsByName } = useAggregationsContext();
  const { enableDefaultMarketCardSubAccount } = useTradingSettings();
  const orderPreset = orderPresetID ? orderPresetsByID.get(orderPresetID) : undefined;

  const { isAuthorized } = useRoleAuth();
  const canViewOrder = useMemo(() => isAuthorized(ACTION.VIEW_ORDER), [isAuthorized]);

  const unifiedLiquidityToken = useUnifiedLiquidity(security?.Symbol);

  const availableSymbols = useUnifiedLiquidityAvailableSymbols({
    marketAccounts: marketAccounts ?? EMPTY_ARRAY,
    markets: markets ?? EMPTY_ARRAY,
    symbol: security?.Symbol,
  });

  // Based on Unified Liquidity, creates list of underlying quote currencies including security symbol.
  const quoteCurrencies = useMemo(() => {
    if (enableUnifiedLiquidity || !unifiedLiquidityToken || !security) {
      return [];
    }

    const quoteCurrenciesSet = new Set<string>();

    for (const availableSymbol of availableSymbols) {
      const availableSecurity = securitiesBySymbol.get(availableSymbol);
      if (availableSecurity) {
        quoteCurrenciesSet.add(availableSecurity.QuoteCurrency);
      }
    }

    return Array.from(quoteCurrenciesSet);
  }, [enableUnifiedLiquidity, unifiedLiquidityToken, security, availableSymbols, securitiesBySymbol]);

  const marketName =
    marketAccounts.length > 0
      ? marketAccountDisplayNameByName.get(marketAccounts[0])
      : marketDisplayNameByName.get(markets[0]);

  const symbolHeaderProps: SymbolDisplayProps = {
    security,
    quoteCurrencies,
    unifiedLiquidity: enableUnifiedLiquidity ? UnifiedLiquidityEnum.Enabled : UnifiedLiquidityEnum.Disabled,
    availableSymbols,
    multipleSelections: marketAccounts.length + markets.length > 1,
    selectedAggregation: aggregation ? aggregationsByName?.get(aggregation) : undefined,
    selectedMarketAccounts: marketAccounts,
    marketName,
    tooltipOnOverflow: true,
  };
  return showSettings ? (
    <PanelHeader justifyContent="center" alignItems="stretch">
      <Flex justifyContent="space-between" alignItems="center">
        <Box>{`${panelTypeLabel} Settings`}</Box>
        <IconButton
          icon={IconName.Close}
          size={FormControlSizes.Small}
          onClick={() => setShowSettings(!showSettings)}
          ghost
          data-testid="single-symbol-panel-settings-close"
        />
      </Flex>
    </PanelHeader>
  ) : (
    <PanelHeader>
      <HStack gap="spacing8" alignItems="center">
        <Flex flex={1}>
          <MarketCardDisplayLabels security={security} />
        </Flex>
        <Stack alignItems="center">
          <SymbolHeader {...symbolHeaderProps} />
        </Stack>
        <HStack flex={1} justifyContent="end" m="-spacing4" data-testid="top-right-items">
          <IconButton
            icon={IconName.Cog}
            title="Market Data Card settings"
            onClick={() => setShowSettings(!showSettings)}
            color="colors.gray.080"
            size={FormControlSizes.Small}
            style={{ marginRight: '-1px' }}
            ghost
            data-testid="single-symbol-panel-settings-button"
          />
        </HStack>
      </HStack>
      <HStack gap="spacing8">
        <Flex gap="spacing2" flex="1 1 0" alignItems="center" overflow="hidden">
          {canViewOrder && orderPreset && (
            <>
              <OrderPresetIcon />
              <EllipsisBoxWithTooltip {...FONT_STYLES}>{orderPreset.name}</EllipsisBoxWithTooltip>
            </>
          )}
        </Flex>
        <Flex flex="1 0 0" justifyContent="center">
          {marketAccounts.length > 0 ? (
            <SymbolSubhead {...symbolHeaderProps} />
          ) : (
            <HStack gap="spacing2">
              <Title>All markets</Title>
              <HelpIcon size={12} color="gray.080" tooltip={marketsTooltip} />
            </HStack>
          )}
        </Flex>
        <Flex gap="spacing2" flex="1 1 0" justifyContent="end" overflow="hidden" alignItems="center">
          {canViewOrder && subAccount && enableDefaultMarketCardSubAccount && (
            <>
              <EllipsisBoxWithTooltip {...FONT_STYLES} textAlign="right">
                {subAccount}
              </EllipsisBoxWithTooltip>
              <SubAccountIcon selection={subAccount} />
            </>
          )}
        </Flex>
      </HStack>
    </PanelHeader>
  );
}
